import React from "react";
import { CURRENCIES } from "../../utils/currencies";

/**
 * Pure, dependency-free formatters that mirror the production helpers but
 * accept the live in-flight settings from the dialog (so the preview reacts
 * to a Select change BEFORE Save). The shipped helpers in `dateHelpers.js`
 * and `formatters.js` read from localStorage — that's the saved state, not
 * the dirty form state, so we duplicate just enough logic here to render an
 * accurate preview line.
 */

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const previewDate = (dateFormat) => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const monthName = MONTHS_SHORT[d.getMonth()];
  switch (dateFormat) {
    case "DD/MM/YYYY":
      return `${dd}/${mm}/${yyyy} · ${dd} ${monthName} ${yyyy}`;
    case "YYYY-MM-DD":
      return `${yyyy}-${mm}-${dd} · ${monthName} ${dd}, ${yyyy}`;
    case "MM/DD/YYYY":
    default:
      return `${mm}/${dd}/${yyyy} · ${monthName} ${dd}, ${yyyy}`;
  }
};

const previewTime = (timeFormat) => {
  const d = new Date();
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  if (timeFormat === "24h") {
    return `${String(h).padStart(2, "0")}:${m}`;
  }
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h % 12 || 12;
  return `${displayH}:${m} ${period}`;
};

// Sample distance — chosen to show meaningful precision in both units.
// 763 mi ≈ 1228 km (a JFK→ORD flight) so the converted value reads cleanly.
const previewDistance = (distanceUnit) => {
  if (distanceUnit === "kilometers") return "1,228 km";
  return "763 mi";
};

const CURRENCY_SYMBOLS = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
  CAD: "C$", AUD: "A$", CHF: "CHF ", INR: "₹",
  KRW: "₩", SGD: "S$", HKD: "HK$", MXN: "MX$",
  BRL: "R$", ZAR: "R", NZD: "NZ$", SEK: "kr",
  NOK: "kr", DKK: "kr", TRY: "₺", THB: "฿",
};

// Sample currency — 1,234.56 in user's home currency. JPY/KRW skip decimals.
const previewCurrency = (homeCurrency) => {
  const code = homeCurrency || "USD";
  const symbol = CURRENCY_SYMBOLS[code] || "";
  const wholeOnly = code === "JPY" || code === "KRW";
  const amt = wholeOnly
    ? Number(1234).toLocaleString("en-US")
    : Number(1234.56).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return symbol ? `${symbol}${amt}` : `${amt} ${code}`;
};

const homeCurrencyName = (code) => {
  const found = CURRENCIES.find((c) => c.code === code);
  return found ? found.name : code;
};

/**
 * Live "this is how your trip data will look" panel for the Settings dialog.
 * Re-renders synchronously as the user toggles selects above.
 */
export const SettingsPreviewPanel = ({ settings }) => {
  const dateFormat = settings.date_format || "MM/DD/YYYY";
  const timeFormat = settings.time_format || "12h";
  const distanceUnit = settings.distance_unit || "miles";
  const homeCurrency = settings.home_currency || "USD";

  return (
    <div
      className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 p-4 space-y-3"
      data-testid="settings-preview-panel"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-sky-900 dark:text-sky-300">
          Live Preview
        </h4>
        <span className="text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400">
          Updates as you change settings
        </span>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
            Today
          </dt>
          <dd
            className="font-medium text-gray-900 dark:text-gray-100 tabular-nums"
            data-testid="settings-preview-date"
          >
            {previewDate(dateFormat)}
          </dd>
        </div>

        <div className="flex items-baseline gap-2">
          <dt className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
            Time now
          </dt>
          <dd
            className="font-medium text-gray-900 dark:text-gray-100 tabular-nums"
            data-testid="settings-preview-time"
          >
            {previewTime(timeFormat)}
          </dd>
        </div>

        <div className="flex items-baseline gap-2">
          <dt className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
            Distance
          </dt>
          <dd
            className="font-medium text-gray-900 dark:text-gray-100 tabular-nums"
            data-testid="settings-preview-distance"
          >
            {previewDistance(distanceUnit)}
          </dd>
        </div>

        <div className="flex items-baseline gap-2">
          <dt className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
            Trip total
          </dt>
          <dd
            className="font-medium text-gray-900 dark:text-gray-100 tabular-nums"
            data-testid="settings-preview-currency"
          >
            {previewCurrency(homeCurrency)}{" "}
            <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
              ({homeCurrencyName(homeCurrency)})
            </span>
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default SettingsPreviewPanel;
